/**
 * The project Brief — one job, summarised.
 *
 * The workspace Brief answers "is the company using LedAI, and what is
 * stuck". This answers the same question about ONE project, and is
 * deliberately SHORTER: four blocks — stats, what has not moved, the walks it
 * came from, and every record on it.
 *
 * ## What is not here, on purpose
 *
 * No adoption panels (a company question, not a job one), no Moved list (the
 * item list below already contains every row, in the same order the stalled
 * panel uses, so a Moved panel would be a second view of rows already on the
 * page), no ball-in-court (removed from the console entirely), and no
 * evidence strip (Project home draws one; two pages about the same project
 * drawing the same strip is duplication, not a summary).
 *
 * ## Nothing on this page is computed here
 *
 * `getProjectBrief` hands down the stalled list, the item list and every stat
 * from ONE read — `stalled` is literally `items.filter(row => row.stalled)`
 * on the server. This view renders both AS GIVEN and sorts neither: a re-sort
 * or a re-filter here would be a second opinion about an order the query
 * already settled, and the two panels sit inches apart.
 *
 * The stall line is printed from `stats.stallThresholdDays` rather than from
 * a constant in this file, because the line is org policy and a hardcoded "3
 * days" would go on reading "3 days" after an admin changed it.
 */

import {
  fetchProjectBrief,
  type ApiError,
  type ConsoleRow,
  type ConsoleWalkResponse,
  type ProjectBriefResponse,
} from "../api";
import { useResource } from "../useResource";
import { C, FONT_MONO, projectDot } from "../tokens";
import {
  Card,
  ConsoleViewProps,
  Eyebrow,
  FailurePanel,
  KindChip,
  LoadingPanel,
  META,
  Pill,
  ROW_LINE,
  ROW_TITLE,
  QuietLine,
  SEVERITY,
  StaleBanner,
  ViewBody,
  ViewHeader,
  ageOf,
  dayLabel,
  metaLine,
  plural,
  whenStamp,
} from "../components/chrome";

/** One of the four numbers across the top. `ink` carries the alarm, exactly
 *  as Project home's does: stalled goes red when it is non-zero. */
function Stat({
  label,
  value,
  sub,
  ink,
  testId,
}: {
  label: string;
  value: string;
  sub: string;
  ink: string;
  testId: string;
}) {
  return (
    <Card style={{ padding: "14px 16px" }} testId={testId}>
      <div style={{ ...META, fontSize: 10.35, color: C.sub2 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 6 }}>
        <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.8px", color: ink }}>
          {value}
        </span>
        <span style={{ fontSize: 13.23, color: C.sub2 }}>{sub}</span>
      </div>
    </Card>
  );
}

function ItemRow({
  row,
  siteName,
  href,
  onOpen,
  first,
  testId,
}: {
  row: ConsoleRow;
  siteName: string | undefined;
  href: string;
  onOpen: () => void;
  first: boolean;
  testId: string;
}) {
  const age = ageOf(row);
  const severity = row.severity ? SEVERITY[row.severity] : undefined;

  return (
    <a
      href={href}
      data-testid={testId}
      data-item-id={row.id}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "11px 4px",
        borderTop: first ? "1px solid transparent" : `1px solid ${C.line}`,
      }}
    >
      <span
        style={{
          width: 34,
          flexShrink: 0,
          fontFamily: FONT_MONO,
          fontSize: 14.37,
          fontWeight: 700,
          color: age.ink,
        }}
      >
        {age.text}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...ROW_LINE, gap: 8 }}>
          <KindChip kind={row.kind} />
          <span
            data-testid="row-title"
            style={{ ...ROW_TITLE, fontSize: 15.52, fontWeight: 600, letterSpacing: "-0.2px" }}
          >
            {row.title}
          </span>
          {severity && <Pill {...severity} />}
        </span>
        <span
          style={{
            ...META,
            display: "block",
            marginTop: 5,
            fontSize: 10.93,
            fontWeight: 400,
            letterSpacing: "0.3px",
            color: C.sub2,
          }}
        >
          {metaLine(row, siteName)}
        </span>
      </span>
    </a>
  );
}

function WalkRow({
  walk,
  href,
  onOpen,
  first,
}: {
  walk: ConsoleWalkResponse;
  href: string;
  onOpen: () => void;
  first: boolean;
}) {
  return (
    <a
      href={href}
      data-testid="project-brief-walk"
      data-walk-id={walk.id}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 4px",
        borderTop: first ? "1px solid transparent" : `1px solid ${C.line}`,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14.95, fontWeight: 600 }}>
          {walk.name || "Untitled walk"}
        </span>
        <span
          style={{
            ...META,
            display: "block",
            marginTop: 4,
            fontSize: 10.93,
            fontWeight: 400,
            letterSpacing: "0.3px",
            color: C.sub2,
          }}
        >
          {/* Absent minutes are "in progress", never `0 MIN` — see
              `walkRecordedMinutes`. */}
          {`${whenStamp(walk.startedAt)} · ${walk.creatorName || walk.creator || "unknown"} · ${
            walk.recordedMinutes === undefined
              ? walk.endedAt
                ? "no recording"
                : "in progress"
              : `${walk.recordedMinutes} min`
          }`}
        </span>
      </span>
      <span
        data-testid="project-brief-walk-items"
        style={{
          ...META,
          fontSize: 10.93,
          flexShrink: 0,
          color: walk.itemCount > 0 ? C.ink : C.sub2,
        }}
      >
        {walk.itemCount > 0 ? plural(walk.itemCount, "record", "records") : "no records"}
      </span>
    </a>
  );
}

export function ProjectBrief(props: ConsoleViewProps & { jobSiteId: string }) {
  const {
    jobSiteId,
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForItem,
    onOpenItem,
    hrefForWalk,
    onOpenWalk,
    hrefForProject,
    onOpenProject,
  } = props;

  const { data, failure, refreshFailure } = useResource<ProjectBriefResponse>(
    `project-brief:${jobSiteId}${search}`,
    () => fetchProjectBrief(jobSiteId, search),
    refreshKey,
  );

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <LoadingPanel shape="project-brief" />;

  const name = data.siteName || data.jobSiteId;
  const { stats } = data;
  const siteName = data.siteName || undefined;
  const walksCapped = data.walks.length < data.walkTotal;

  return (
    <>
      <ViewHeader
        crumb={
          stats.lastWalkAt
            ? `Project brief · last walked ${dayLabel(stats.lastWalkAt)}`
            : "Project brief · never walked"
        }
        count={`${plural(stats.stalled, "item", "items")} past the stall line`}
        title={name}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            flexWrap: "wrap",
            margin: "14px 0 0",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 5,
              background: projectDot(data.jobSiteId),
              flexShrink: 0,
            }}
          />
          {/* Back to the project's own page. A brief is a summary OF
              something, and a summary with no way to the thing is a dead
              end. */}
          <a
            href={hrefForProject(data.jobSiteId)}
            data-testid="project-brief-home-link"
            onClick={(event) => {
              event.preventDefault();
              onOpenProject(data.jobSiteId);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 32,
              padding: "0 13px",
              borderRadius: 10,
              background: C.surface,
              border: `1px solid ${C.line2}`,
              fontSize: 14.37,
              fontWeight: 600,
            }}
          >
            Project home
          </a>
        </div>
      </ViewHeader>

      <ViewBody>
        {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

        {/* ---------- stats ---------- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <Stat
            label="Open items"
            value={String(stats.openItems)}
            sub="not closed"
            ink={C.ink}
            testId="project-brief-stat-open"
          />
          <Stat
            label="Hasn't moved"
            value={String(stats.stalled)}
            // The line is org policy, printed from what the server used.
            sub={`${stats.stallThresholdDays}+ days`}
            ink={stats.stalled > 0 ? C.red : C.ink}
            testId="project-brief-stat-stalled"
          />
          <Stat
            label="Records"
            value={String(stats.records)}
            sub="from walks"
            ink={C.ink}
            testId="project-brief-stat-records"
          />
          <Stat
            label="Walks"
            value={String(stats.walks)}
            sub={stats.lastWalkAt ? `last ${dayLabel(stats.lastWalkAt)}` : "never walked"}
            ink={C.ink}
            testId="project-brief-stat-walks"
          />
        </div>

        {/* ---------- hasn't moved ---------- */}
        <div style={{ marginBottom: 22 }}>
          <Eyebrow
            right={
              <span style={{ ...META, fontSize: 10.35, color: C.sub2 }}>
                {`${plural(stats.stalled, "item", "items")} · longest first`}
              </span>
            }
          >
            Hasn't moved
          </Eyebrow>

          {data.stalled.length === 0 ? (
            <QuietLine>
              {`Nothing on this project is past the ${plural(
                stats.stallThresholdDays,
                "day",
                "day",
              )} stall line.`}
            </QuietLine>
          ) : (
            <Card testId="project-brief-stalled">
              {data.stalled.map((row: ConsoleRow, index: number) => (
                <ItemRow
                  key={row.id}
                  row={row}
                  siteName={siteName}
                  href={hrefForItem(row.id)}
                  onOpen={() => onOpenItem(row.id)}
                  first={index === 0}
                  testId="project-brief-stalled-row"
                />
              ))}
            </Card>
          )}
        </div>

        {/* ---------- walks ---------- */}
        <div style={{ marginBottom: 22 }}>
          <Eyebrow
            right={
              <span style={{ ...META, fontSize: 10.35, color: C.sub2 }} data-testid="project-brief-walk-total">
                {/* The TOTAL, always — a capped list that did not say so
                    would read as "that is all there is". */}
                {walksCapped
                  ? `showing ${data.walks.length} of ${plural(data.walkTotal, "walk", "walks")}`
                  : `${plural(data.walkTotal, "walk", "walks")} · newest first`}
              </span>
            }
          >
            Walks
          </Eyebrow>

          {data.walks.length === 0 ? (
            <QuietLine>
              This project has never been walked. Everything above fills in from the first walk.
            </QuietLine>
          ) : (
            <Card testId="project-brief-walks">
              {data.walks.map((walk, index) => (
                <WalkRow
                  key={walk.id}
                  walk={walk}
                  href={hrefForWalk(walk.id)}
                  onOpen={() => onOpenWalk(walk.id)}
                  first={index === 0}
                />
              ))}
            </Card>
          )}
        </div>

        {/* ---------- every record ---------- */}
        <div>
          <Eyebrow
            right={
              <span style={{ ...META, fontSize: 10.35, color: C.sub2 }}>
                {`${plural(data.items.length, "record", "records")} · longest first`}
              </span>
            }
          >
            Records
          </Eyebrow>

          {data.items.length === 0 ? (
            <QuietLine>
              No records on this project yet. They appear as soon as a walk is processed.
            </QuietLine>
          ) : (
            <Card testId="project-brief-items">
              {data.items.map((row: ConsoleRow, index: number) => (
                <ItemRow
                  key={row.id}
                  row={row}
                  siteName={siteName}
                  href={hrefForItem(row.id)}
                  onOpen={() => onOpenItem(row.id)}
                  first={index === 0}
                  testId="project-brief-item-row"
                />
              ))}
            </Card>
          )}
        </div>
      </ViewBody>
    </>
  );
}
